import { DownOutlined } from '@ant-design/icons';
import AppCard from '@nxsys/components/AppCard';
import AppCardTabListContainer from '@nxsys/components/AppCardTabListContainer';
import AppCollapse from '@nxsys/components/AppCollapse';
import AppDescription from '@nxsys/components/AppDescription';
import AppSelect from '@nxsys/components/AppSelect';
import AppTableContainer from '@nxsys/components/AppTableContainer';
import { useGetRateConfigs } from '@nxsys/hooks/api/kid/use-umbrella-rate-calculator';
import { AppState } from '@nxsys/store';
import { Icons } from '@nxsys/types/icons';
import { Option } from '@nxsys/types/models/Common';
import { showCurrencyByCountry } from '@nxsys/utils';
import { getTaxYearRateThresholdOptions } from '@nxsys/utils/taxYear';
import { Col, Descriptions, Row, Spin } from 'antd';
import { ColumnsType } from 'antd/lib/table';
import { useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import dataSubTable from './data.json';

/** Extract the starting tax year number from a "2026-2027" string */
const parseTaxYearNumber = (taxYearValue: string): number =>
  Number(taxYearValue?.split('-')[0]);

const RateBands: React.FC = () => {
  return (
    <div className="RateBands space-y-[24px]">
      <NMWRates />
      <TaxBand />
      <NIBandRates />
      <SSP_SMP_SPP_Other_Rate />
      <MileageRates />
      <AttachmentOrderList />
    </div>
  );
};

const NMWRates = () => {
  const taxYearOptions = getTaxYearRateThresholdOptions();
  const [selectedTaxYear, setSelectedTaxYear] = useState<Option<string>>(
    taxYearOptions[0],
  );
  const { value: rateConfigs, loading, run: fetchRates } = useGetRateConfigs();

  useEffect(() => {
    if (selectedTaxYear?.value) {
      fetchRates(parseTaxYearNumber(selectedTaxYear.value));
    }
  }, [selectedTaxYear]);

  const data = useMemo(
    () => rateConfigs?.nmwRates?.fields ?? [],
    [rateConfigs],
  );

  const columns: ColumnsType<{ label: string; value: number }> = [
    {
      title: '',
      dataIndex: 'label',
      key: 'label',
      width: 200,
    },
    {
      title: 'Rate',
      dataIndex: 'value',
      key: 'value',
      align: 'right',
    },
  ];

  return (
    <AppCard
      title={
        <div className="flex items-center justify-between">
          <div>NMW Rates</div>
          <div>
            <AppSelect
              options={taxYearOptions}
              onChange={(value: Option<string>) => setSelectedTaxYear(value)}
              defaultValue={taxYearOptions[0]}
            />
          </div>
        </div>
      }
      lineTitleColor="#FFE785"
    >
      <Spin spinning={loading}>
        <AppTableContainer
          rowKey="key"
          columns={columns}
          data={data}
          loading={false}
          pagination={false}
          page={1}
          total={data.length}
          pageSize={data.length}
          scroll={{ x: 400 }}
        />
      </Spin>
    </AppCard>
  );
};

const TaxBand = () => {
  const taxYearOptions = getTaxYearRateThresholdOptions();
  const [selectedTaxYear, setSelectedTaxYear] = useState<Option<string>>(
    taxYearOptions[0],
  );
  const { value: rateConfigs, loading, run: fetchRates } = useGetRateConfigs();

  useEffect(() => {
    if (selectedTaxYear?.value) {
      fetchRates(parseTaxYearNumber(selectedTaxYear.value));
    }
  }, [selectedTaxYear]);

  const countryOptions = useMemo(() => {
    return [
      { label: 'England & Northern Ireland', value: 'England' },
      { label: 'Scotland', value: 'Scotland' },
      { label: 'Wales', value: 'Wales' },
    ];
  }, []);
  const [selectedCountry, setSelectedCountry] = useState<Option<string>>(
    countryOptions[0],
  );

  const emptyBand = { data: [], meta: { upperLimit: '-', taxFreeAllowance: '-' } };

  const data = useMemo(() => {
    if (!rateConfigs?.taxBands) return emptyBand;
    const { england, scotland } = rateConfigs.taxBands;
    return selectedCountry.value === 'Scotland'
      ? (scotland ?? emptyBand)
      : (england ?? emptyBand);
  }, [rateConfigs, selectedCountry]);

  const columns: ColumnsType<{
    bandWidth: string;
    from: string;
    to: string;
    rate: string;
    basicRate: boolean;
  }> = [
    { title: 'Bandwidth', dataIndex: 'bandWidth', key: 'bandWidth' },
    { title: 'From', dataIndex: 'from', key: 'from' },
    { title: 'To', dataIndex: 'to', key: 'to' },
    { title: 'Rate %', dataIndex: 'rate', key: 'rate' },
    {
      title: 'Basic Rate?',
      dataIndex: 'basicRate',
      key: 'basicRate',
      render: (value) =>
        value ? <Icons.IconCheckCircle /> : <Icons.IconCloseCircle />,
    },
  ];

  return (
    <AppCard
      title={
        <div className="flex items-center justify-between">
          <div>Tax Bands</div>
          <div className="flex items-center gap-[16px]">
            <AppSelect
              options={countryOptions}
              onChange={(value: Option<string>) => setSelectedCountry(value)}
              defaultValue={countryOptions[0]}
            />
            <AppSelect
              options={taxYearOptions}
              onChange={(value: Option<string>) => setSelectedTaxYear(value)}
              defaultValue={taxYearOptions[0]}
            />
          </div>
        </div>
      }
      lineTitleColor="#FFE785"
    >
      <Spin spinning={loading}>
        <AppTableContainer
          rowKey="key"
          columns={columns}
          data={data.data}
          loading={false}
          pagination={false}
          page={1}
          total={data.data.length}
          pageSize={data.data.length}
          scroll={{ x: 400 }}
        />
        <div className="max-w-[360px] space-y-[16px] py-[12px] px-[16px] bg-neutral-50 rounded-[8px] mt-[16px]">
          <AppDescription
            column={1}
            labelStyle={{ width: 216 }}
            bordered={false}
            colon={false}
            size="middle"
          >
            <Descriptions.Item label="Upper (Regulatory) limit %">
              {data.meta.upperLimit}
            </Descriptions.Item>
            <Descriptions.Item label="Tax Free Allowance (Annual)">
              {data.meta.taxFreeAllowance}
            </Descriptions.Item>
          </AppDescription>
        </div>
      </Spin>
    </AppCard>
  );
};

const NIBandRates = () => {
  const { payrollCountry } = useSelector((state: AppState) => state.appStore);
  const taxYearOptions = getTaxYearRateThresholdOptions();
  const [selectedTaxYear, setSelectedTaxYear] = useState<Option<string>>(
    taxYearOptions[0],
  );
  const { value: rateConfigs, loading, run: fetchRates } = useGetRateConfigs();

  useEffect(() => {
    if (selectedTaxYear?.value) {
      fetchRates(parseTaxYearNumber(selectedTaxYear.value));
    }
  }, [selectedTaxYear]);

  const tabs = useMemo(
    () => [
      {
        tab: 'Categories',
        key: 'CATEGORIES',
      },
      {
        tab: 'Band Width',
        key: 'BAND_WIDTH',
      },
      {
        tab: 'Rates',
        key: 'RATES',
      },
    ],
    [],
  );

  const [activeTab, setActiveTab] = useState('CATEGORIES');

  const handleTabChange = (key: string) => {
    setActiveTab(key);
  };

  const dataCategories = {
    other: [
      {
        key: '1',
        letter: 'A',
        des: 'Standard Rate Contributions',
      },
      {
        key: '2',
        letter: 'B',
        des: 'Reduced Rate Contributions',
      },
      {
        key: '3',
        letter: 'C',
        des: 'Employer Only Contributions',
      },
      {
        key: '4',
        letter: 'H',
        des: 'Not contracted-out standard rate contributions for under 25',
      },
      {
        key: '5',
        letter: 'J',
        des: 'Employer Only Contributions',
      },
      {
        key: '6',
        letter: 'M',
        des: 'Not contracted-out standard rate contributions for under 21',
      },
      {
        key: '7',
        letter: 'X',
        des: 'No Contributions',
      },
      {
        key: '8',
        letter: 'Z',
        des: 'Not contracted-out deferred rate contributions for under 21',
      },
    ],
    '2023-2024': [
      {
        key: '1',
        letter: 'A',
        des: 'Standard Rate Contributions',
      },
      {
        key: '2',
        letter: 'B',
        des: 'Reduced Rate Contributions',
      },
      {
        key: '3',
        letter: 'C',
        des: 'Employer Only Contributions',
      },
      {
        key: '4',
        letter: 'F',
        des: 'All employees who work in freeports, apart from those in groups I, L, and S in this table',
      },
      {
        key: '5',
        letter: 'H',
        des: 'Not contracted-out standard rate contributions for under 25',
      },
      {
        key: '6',
        letter: 'I',
        des: 'Married women and widows who work in freeports and are entitled to pay reduced National Insurance',
      },
      {
        key: '7',
        letter: 'J',
        des: 'Employer Only Contributions',
      },
      {
        key: '8',
        letter: 'L',
        des: '	Employees who work in freeports and can defer National Insurance because they’re already paying it in another job',
      },
      {
        key: '9',
        letter: 'M',
        des: 'Not contracted-out standard rate contributions for under 21',
      },
      {
        key: '10',
        letter: 'S',
        des: 'Employees who work in freeports and are over the State Pension age',
      },
      {
        key: '11',
        letter: 'V',
        des: 'Employees who are working in their first job since leaving the armed forces (veterans)',
      },
      {
        key: '12',
        letter: 'Z',
        des: 'Not contracted-out deferred rate contributions for under 21',
      },
    ],
    '2024-2025': [
      {
        key: '1',
        letter: 'A',
        des: 'Standard Rate Contributions',
      },
      {
        key: '2',
        letter: 'B',
        des: 'Reduced Rate Contributions',
      },
      {
        key: '3',
        letter: 'C',
        des: 'Employer Only Contributions',
      },
      {
        key: '4',
        letter: 'F',
        des: 'All employees who work in freeports, apart from those in groups I, L, and S in this table',
      },
      {
        key: '5',
        letter: 'H',
        des: 'Not contracted-out standard rate contributions for under 25',
      },
      {
        key: '6',
        letter: 'I',
        des: 'Married women and widows who work in freeports and are entitled to pay reduced National Insurance',
      },
      {
        key: '7',
        letter: 'J',
        des: 'Employer Only Contributions',
      },
      {
        key: '8',
        letter: 'L',
        des: '	Employees who work in freeports and can defer National Insurance because they’re already paying it in another job',
      },
      {
        key: '9',
        letter: 'M',
        des: 'Not contracted-out standard rate contributions for under 21',
      },
      {
        key: '10',
        letter: 'S',
        des: 'Employees who work in freeports and are over the State Pension age',
      },
      {
        key: '11',
        letter: 'V',
        des: 'Employees who are working in their first job since leaving the armed forces (veterans)',
      },
      {
        key: '12',
        letter: 'Z',
        des: 'Not contracted-out deferred rate contributions for under 21',
      },
    ],
    '2025-2026': [
      {
        key: '1',
        letter: 'A',
        des: 'Standard Rate Contributions',
      },
      {
        key: '2',
        letter: 'B',
        des: 'Reduced Rate Contributions',
      },
      {
        key: '3',
        letter: 'C',
        des: 'Employer Only Contributions',
      },
      {
        key: '13',
        letter: 'D',
        des: 'Investment Zone — deferment',
      },
      {
        key: '14',
        letter: 'E',
        des: 'Investment Zone — married women and widows reduced rate',
      },
      {
        key: '4',
        letter: 'F',
        des: 'All employees who work in freeports, apart from those in groups I, L, and S in this table',
      },
      {
        key: '5',
        letter: 'H',
        des: 'Not contracted-out standard rate contributions for under 25',
      },
      {
        key: '6',
        letter: 'I',
        des: 'Married women and widows who work in freeports and are entitled to pay reduced National Insurance',
      },
      {
        key: '7',
        letter: 'J',
        des: 'Employer Only Contributions',
      },
      {
        key: '15',
        letter: 'K',
        des: 'Investment Zone — state pensioner',
      },
      {
        key: '8',
        letter: 'L',
        des: '	Employees who work in freeports and can defer National Insurance because they’re already paying it in another job',
      },
      {
        key: '9',
        letter: 'M',
        des: 'Not contracted-out standard rate contributions for under 21',
      },
      {
        key: '16',
        letter: 'N',
        des: 'Investment Zone',
      },
      {
        key: '10',
        letter: 'S',
        des: 'Employees who work in freeports and are over the State Pension age',
      },
      {
        key: '11',
        letter: 'V',
        des: 'Employees who are working in their first job since leaving the armed forces (veterans)',
      },
      {
        key: '12',
        letter: 'Z',
        des: 'Not contracted-out deferred rate contributions for under 21',
      },
    ],
  };

  /** BandWidth data comes from API; derive quarterly and halfyearly from monthly */
  const dataBandWidth = useMemo(() => {
    return (rateConfigs?.niEarningLimits ?? []).map((item) => ({
      ...item,
      quarterly: (parseFloat(item.monthly) * 3).toFixed(2),
      halfyearly: (parseFloat(item.monthly) * 6).toFixed(2),
    }));
  }, [rateConfigs]);

  const dataEmployee = {
    other: [
      {
        key: '1',
        level0: 'N/A',
        level1: '0%',
        level2: '12%',
        level3: '2%',
        categories: 'Ai',
      },
      {
        key: '2',
        level0: 'N/A',
        level1: '0%',
        level2: '5.85%',
        level3: '2%',
        categories: 'Bi',
      },
      {
        key: '3',
        level0: 'N/A',
        level1: 'N/A',
        level2: 'N/A',
        level3: 'N/A',
        categories: 'C(Pensioners)i',
      },
      {
        key: '4',
        level0: 'N/A',
        level1: '0%',
        level2: '12%',
        level3: '2%',
        categories: 'H(Apprentice Under 25)i',
      },
      {
        key: '5',
        level0: 'N/A',
        level1: '0%',
        level2: '2%',
        level3: '2%',
        categories: 'J(Deferment)i',
      },
      {
        key: '6',
        level0: 'N/A',
        level1: '0%',
        level2: '12%',
        level3: '2%',
        categories: 'M(Under 21)i',
      },
      {
        key: '7',
        level0: 'N/A',
        level1: '0%',
        level2: '2%',
        level3: '2%',
        categories: 'Z(Under 21-Deferment)i',
      },
    ],
    '2023-2024': [
      {
        key: '1',
        level0: 'N/A',
        level1: '0%',
        level2: '12%',
        level3: '2%',
        categories: 'Ai',
      },
      {
        key: '2',
        level0: 'N/A',
        level1: '0%',
        level2: '5.85%',
        level3: '2%',
        categories: 'Bi',
      },
      {
        key: '3',
        level0: 'N/A',
        level1: 'N/A',
        level2: 'N/A',
        level3: 'N/A',
        categories: 'C(Pensioners)i',
      },
      {
        key: '4',
        level0: 'N/A',
        level1: '0%',
        level2: '12%',
        level3: '2%',
        categories: 'F(Freeport Standard)i',
      },
      {
        key: '5',
        level0: 'N/A',
        level1: '0%',
        level2: '12%',
        level3: '2%',
        categories: 'H(Apprentice Under 25)i',
      },
      {
        key: '6',
        level0: 'N/A',
        level1: '0%',
        level2: '5.85%',
        level3: '2%',
        categories: 'I(Freeport MWRRE)i',
      },
      {
        key: '7',
        level0: 'N/A',
        level1: '0%',
        level2: '2%',
        level3: '2%',
        categories: 'J(Deferment)i',
      },
      {
        key: '8',
        level0: 'N/A',
        level1: '0%',
        level2: '2%',
        level3: '2%',
        categories: 'L(Freeport Deferment)i',
      },
      {
        key: '9',
        level0: 'N/A',
        level1: '0%',
        level2: '12%',
        level3: '2%',
        categories: 'M(Under 21)i',
      },
      {
        key: '10',
        level0: 'N/A',
        level1: 'N/A',
        level2: 'N/A',
        level3: 'N/A',
        categories: 'S(Freeport Pensioners)i',
      },
      {
        key: '11',
        level0: 'N/A',
        level1: '0%',
        level2: '12%',
        level3: '2%',
        categories: 'V(Veterans Standard)i',
      },
      {
        key: '12',
        level0: 'N/A',
        level1: '0%',
        level2: '2%',
        level3: '2%',
        categories: 'Z(Under 21 - Deferment)i',
      },
    ],
    '2024-2025': [
      {
        key: '1',
        level0: 'N/A',
        level1: '0%',
        level2: '8%',
        level3: '2%',
        categories: 'Ai',
      },
      {
        key: '2',
        level0: 'N/A',
        level1: '0%',
        level2: '1.85%',
        level3: '2%',
        categories: 'Bi',
      },
      {
        key: '3',
        level0: 'N/A',
        level1: 'N/A',
        level2: 'N/A',
        level3: 'N/A',
        categories: 'C(Pensioners)i',
      },
      {
        key: '4',
        level0: 'N/A',
        level1: '0%',
        level2: '8%',
        level3: '2%',
        categories: 'F(Freeport Standard)i',
      },
      {
        key: '5',
        level0: 'N/A',
        level1: '0%',
        level2: '8%',
        level3: '2%',
        categories: 'H(Apprentice Under 25)i',
      },
      {
        key: '6',
        level0: 'N/A',
        level1: '0%',
        level2: '1.85%',
        level3: '2%',
        categories: 'I(Freeport MWRRE)i',
      },
      {
        key: '7',
        level0: 'N/A',
        level1: '0%',
        level2: '2%',
        level3: '2%',
        categories: 'J(Deferment)i',
      },
      {
        key: '8',
        level0: 'N/A',
        level1: '0%',
        level2: '2%',
        level3: '2%',
        categories: 'L(Freeport Deferment)i',
      },
      {
        key: '9',
        level0: 'N/A',
        level1: '0%',
        level2: '8%',
        level3: '2%',
        categories: 'M(Under 21)i',
      },
      {
        key: '10',
        level0: 'N/A',
        level1: 'N/A',
        level2: 'N/A',
        level3: 'N/A',
        categories: 'S(Freeport Pensioners)i',
      },
      {
        key: '11',
        level0: 'N/A',
        level1: '0%',
        level2: '8%',
        level3: '2%',
        categories: 'V(Veterans Standard)i',
      },
      {
        key: '12',
        level0: 'N/A',
        level1: '0%',
        level2: '2%',
        level3: '2%',
        categories: 'Z(Under 21 - Deferment)i',
      },
    ],
    '2025-2026': [
      {
        key: '1',
        level0: 'N/A',
        level1: '0%',
        level2: '8%',
        level3: '2%',
        categories: 'Ai',
      },
      {
        key: '2',
        level0: 'N/A',
        level1: '0%',
        level2: '1.85%',
        level3: '2%',
        categories: 'Bi',
      },
      {
        key: '3',
        level0: 'N/A',
        level1: 'N/A',
        level2: 'N/A',
        level3: 'N/A',
        categories: 'C(Pensioners)i',
      },
      {
        key: '13',
        level0: 'N/A',
        level1: '0%',
        level2: '2%',
        level3: '2%',
        categories: 'D(Investment Zone — deferment)i',
      },
      {
        key: '14',
        level0: 'N/A',
        level1: '0%',
        level2: '1.85%',
        level3: '2%',
        categories:
          'E(Investment Zone — married women and widows reduced rate)i',
      },
      {
        key: '4',
        level0: 'N/A',
        level1: '0%',
        level2: '8%',
        level3: '2%',
        categories: 'F(Freeport Standard)i',
      },
      {
        key: '5',
        level0: 'N/A',
        level1: '0%',
        level2: '8%',
        level3: '2%',
        categories: 'H(Apprentice Under 25)i',
      },
      {
        key: '6',
        level0: 'N/A',
        level1: '0%',
        level2: '1.85%',
        level3: '2%',
        categories: 'I(Freeport MWRRE)i',
      },
      {
        key: '7',
        level0: 'N/A',
        level1: '0%',
        level2: '2%',
        level3: '2%',
        categories: 'J(Deferment)i',
      },
      {
        key: '15',
        level0: 'N/A',
        level1: 'N/A',
        level2: 'N/A',
        level3: 'N/A',
        categories: 'K(Investment Zone — state pensioner)i',
      },
      {
        key: '8',
        level0: 'N/A',
        level1: '0%',
        level2: '2%',
        level3: '2%',
        categories: 'L(Freeport Deferment)i',
      },
      {
        key: '9',
        level0: 'N/A',
        level1: '0%',
        level2: '8%',
        level3: '2%',
        categories: 'M(Under 21)i',
      },
      {
        key: '16',
        level0: 'N/A',
        level1: '0%',
        level2: '8%',
        level3: '2%',
        categories: 'N(Investment Zone)i',
      },
      {
        key: '10',
        level0: 'N/A',
        level1: 'N/A',
        level2: 'N/A',
        level3: 'N/A',
        categories: 'S(Freeport Pensioners)i',
      },
      {
        key: '11',
        level0: 'N/A',
        level1: '0%',
        level2: '8%',
        level3: '2%',
        categories: 'V(Veterans Standard)i',
      },
      {
        key: '12',
        level0: 'N/A',
        level1: '0%',
        level2: '2%',
        level3: '2%',
        categories: 'Z(Under 21 - Deferment)i',
      },
    ],
  };

  const dataEmployer = {
    other: [
      {
        key: '1',
        level0: 'N/A',
        level1: '0%',
        level2: '13.8%',
        level3: '13.8%',
        categories: 'Ai',
      },
      {
        key: '2',
        level0: 'N/A',
        level1: '0%',
        level2: '13.8%',
        level3: '13.8%',
        categories: 'Bi',
      },
      {
        key: '3',
        level0: 'N/A',
        level1: '0%',
        level2: '13.8%',
        level3: '13.8%',
        categories: 'C(Pensioners)i',
      },
      {
        key: '4',
        level0: 'N/A',
        level1: '0%',
        level2: '0%',
        level3: '13.8%',
        categories: 'H(Apprentice Under 25)i',
      },
      {
        key: '5',
        level0: 'N/A',
        level1: '0%',
        level2: '13.8%',
        level3: '13.8%',
        categories: 'J(Deferment)i',
      },
      {
        key: '6',
        level0: 'N/A',
        level1: '0%',
        level2: '0%',
        level3: '13.8%',
        categories: 'M(Under 21)i',
      },
      {
        key: '7',
        level0: 'N/A',
        level1: '0%',
        level2: '0%',
        level3: '13.8%',
        categories: 'Z(Under 21-Deferment)i',
      },
    ],
    '2023-2024': [
      {
        key: '1',
        level0: 'N/A',
        level1: '0%',
        level2: '13.8%',
        level3: '13.8%',
        level4: '13.8%',
        categories: 'Ai',
      },
      {
        key: '2',
        level0: 'N/A',
        level1: '0%',
        level2: '13.8%',
        level3: '13.8%',
        level4: '13.8%',
        categories: 'Bi',
      },
      {
        key: '3',
        level0: 'N/A',
        level1: '0%',
        level2: '13.8%',
        level3: '13.8%',
        level4: '13.8%',
        categories: 'C(Pensioners)i',
      },
      {
        key: '4',
        level0: 'N/A',
        level1: '0%',
        level2: '0%',
        level3: '13.8%',
        level4: '13.8%',
        categories: 'F(Freeport Standard)i',
      },
      {
        key: '5',
        level0: 'N/A',
        level1: '0%',
        level2: '0%',
        level3: '0%',
        level4: '13.8%',
        categories: 'H(Apprentice Under 25)i',
      },
      {
        key: '6',
        level0: 'N/A',
        level1: '0%',
        level2: '0%',
        level3: '13.8%',
        level4: '13.8%',
        categories: 'I(Freeport MWRRE)i',
      },
      {
        key: '7',
        level0: 'N/A',
        level1: '0%',
        level2: '13.8%',
        level3: '13.8%',
        level4: '13.8%',
        categories: 'J(Deferment)i',
      },
      {
        key: '8',
        level0: 'N/A',
        level1: '0%',
        level2: '0%',
        level3: '13.8%',
        level4: '13.8%',
        categories: 'L(Freeport Deferment)i',
      },
      {
        key: '9',
        level0: 'N/A',
        level1: '0%',
        level2: '0%',
        level3: '0%',
        level4: '13.8%',
        categories: 'M(Under 21)i',
      },
      {
        key: '10',
        level0: 'N/A',
        level1: '0%',
        level2: '0%',
        level3: '13.8%',
        level4: '13.8%',
        categories: 'S(Freeport Pensioners)i',
      },
      {
        key: '11',
        level0: 'N/A',
        level1: '0%',
        level2: '0%',
        level3: '0%',
        level4: '13.8%',
        categories: 'V(Veterans Standard)i',
      },
      {
        key: '12',
        level0: 'N/A',
        level1: '0%',
        level2: '0%',
        level3: '0%',
        level4: '13.8%',
        categories: 'Z(Under 21 - Deferment)i',
      },
    ],
    '2024-2025': [
      {
        key: '1',
        level0: 'N/A',
        level1: '0%',
        level2: '13.8%',
        level3: '13.8%',
        level4: '13.8%',
        categories: 'Ai',
      },
      {
        key: '2',
        level0: 'N/A',
        level1: '0%',
        level2: '13.8%',
        level3: '13.8%',
        level4: '13.8%',
        categories: 'Bi',
      },
      {
        key: '3',
        level0: 'N/A',
        level1: '0%',
        level2: '13.8%',
        level3: '13.8%',
        level4: '13.8%',
        categories: 'C(Pensioners)i',
      },
      {
        key: '4',
        level0: 'N/A',
        level1: '0%',
        level2: '0%',
        level3: '13.8%',
        level4: '13.8%',
        categories: 'F(Freeport Standard)i',
      },
      {
        key: '5',
        level0: 'N/A',
        level1: '0%',
        level2: '0%',
        level3: '0%',
        level4: '13.8%',
        categories: 'H(Apprentice Under 25)i',
      },
      {
        key: '6',
        level0: 'N/A',
        level1: '0%',
        level2: '0%',
        level3: '13.8%',
        level4: '13.8%',
        categories: 'I(Freeport MWRRE)i',
      },
      {
        key: '7',
        level0: 'N/A',
        level1: '0%',
        level2: '13.8%',
        level3: '13.8%',
        level4: '13.8%',
        categories: 'J(Deferment)i',
      },
      {
        key: '8',
        level0: 'N/A',
        level1: '0%',
        level2: '0%',
        level3: '13.8%',
        level4: '13.8%',
        categories: 'L(Freeport Deferment)i',
      },
      {
        key: '9',
        level0: 'N/A',
        level1: '0%',
        level2: '0%',
        level3: '0%',
        level4: '13.8%',
        categories: 'M(Under 21)i',
      },
      {
        key: '10',
        level0: 'N/A',
        level1: '0%',
        level2: '0%',
        level3: '13.8%',
        level4: '13.8%',
        categories: 'S(Freeport Pensioners)i',
      },
      {
        key: '11',
        level0: 'N/A',
        level1: '0%',
        level2: '0%',
        level3: '0%',
        level4: '13.8%',
        categories: 'V(Veterans Standard)i',
      },
      {
        key: '12',
        level0: 'N/A',
        level1: '0%',
        level2: '0%',
        level3: '0%',
        level4: '13.8%',
        categories: 'Z(Under 21 - Deferment)i',
      },
    ],
    '2025-2026': [
      {
        key: '1',
        level0: 'N/A',
        level1: '15%',
        level2: '15%',
        level3: '15%',
        level4: '15%',
        categories: 'Ai',
      },
      {
        key: '2',
        level0: 'N/A',
        level1: '15%',
        level2: '15%',
        level3: '15%',
        level4: '15%',
        categories: 'Bi',
      },
      {
        key: '3',
        level0: 'N/A',
        level1: '15%',
        level2: '15%',
        level3: '15%',
        level4: '15%',
        categories: 'C(Pensioners)i',
      },
      {
        key: '13',
        level0: 'N/A',
        level1: '0%',
        level2: '0%',
        level3: '15%',
        level4: '15%',
        categories: 'D(Investment Zone — deferment)i',
      },
      {
        key: '14',
        level0: 'N/A',
        level1: '0%',
        level2: '0%',
        level3: '15%',
        level4: '15%',
        categories:
          'E(Investment Zone — married women and widows reduced rate)i',
      },
      {
        key: '4',
        level0: 'N/A',
        level1: '0%',
        level2: '0%',
        level3: '15%',
        level4: '15%',
        categories: 'F(Freeport Standard)i',
      },
      {
        key: '5',
        level0: 'N/A',
        level1: '0%',
        level2: '0%',
        level3: '0%',
        level4: '15%',
        categories: 'H(Apprentice Under 25)i',
      },
      {
        key: '6',
        level0: 'N/A',
        level1: '0%',
        level2: '0%',
        level3: '15%',
        level4: '15%',
        categories: 'I(Freeport MWRRE)i',
      },
      {
        key: '7',
        level0: 'N/A',
        level1: '15%',
        level2: '15%',
        level3: '15%',
        level4: '15%',
        categories: 'J(Deferment)i',
      },
      {
        key: '15',
        level0: 'N/A',
        level1: '0%',
        level2: '0%',
        level3: '15%',
        level4: '15%',
        categories: 'K(Investment Zone — state pensioner)i',
      },
      {
        key: '8',
        level0: 'N/A',
        level1: '0%',
        level2: '0%',
        level3: '15%',
        level4: '15%',
        categories: 'L(Freeport Deferment)i',
      },
      {
        key: '9',
        level0: 'N/A',
        level1: '0%',
        level2: '0%',
        level3: '0%',
        level4: '15%',
        categories: 'M(Under 21)i',
      },
      {
        key: '16',
        level0: 'N/A',
        level1: '0%',
        level2: '0%',
        level3: '15%',
        level4: '15%',
        categories: 'N(Investment Zone)i',
      },
      {
        key: '10',
        level0: 'N/A',
        level1: '0%',
        level2: '0%',
        level3: '15%',
        level4: '15%',
        categories: 'S(Freeport Pensioners)i',
      },
      {
        key: '11',
        level0: 'N/A',
        level1: '0%',
        level2: '0%',
        level3: '0%',
        level4: '15%',
        categories: 'V(Veterans Standard)i',
      },
      {
        key: '12',
        level0: 'N/A',
        level1: '0%',
        level2: '0%',
        level3: '0%',
        level4: '15%',
        categories: 'Z(Under 21 - Deferment)i',
      },
    ],
  };

  const columnCategories = [
    {
      title: 'Letter',
      dataIndex: 'letter',
      key: 'letter',
      width: 100,
    },
    {
      title: 'Description',
      dataIndex: 'des',
      key: 'des',
    },
  ];

  const columnBandWidth = [
    {
      title: '',
      dataIndex: 'key',
      key: 'key',
      width: 60,
    },
    {
      title: 'Band Description',
      dataIndex: 'des',
      key: 'des',
      width: 310,
    },
    {
      title: `Weekly (${showCurrencyByCountry(payrollCountry)})`,
      dataIndex: 'weekly',
      key: 'weekly',
      width: 150,
    },
    {
      title: `2-Weekly (${showCurrencyByCountry(payrollCountry)})`,
      dataIndex: 'weekly2',
      key: 'weekly2',
      width: 150,
    },
    {
      title: `4-Weekly (${showCurrencyByCountry(payrollCountry)})`,
      dataIndex: 'weekly4',
      key: 'weekly4',
      width: 150,
    },
    {
      title: `Monthly (${showCurrencyByCountry(payrollCountry)})`,
      dataIndex: 'monthly',
      key: 'monthly',
      width: 150,
    },
    {
      title: `Quarterly (${showCurrencyByCountry(payrollCountry)})`,
      dataIndex: 'quarterly',
      key: 'quarterly',
      width: 150,
    },
    {
      title: `Haft-yearly (${showCurrencyByCountry(payrollCountry)})`,
      dataIndex: 'halfyearly',
      key: 'halfyearly',
      width: 150,
    },
    {
      title: `Annually (${showCurrencyByCountry(payrollCountry)})`,
      dataIndex: 'annually',
      key: 'annually',
      width: 150,
    },
  ];

  const columnEmployee = [
    {
      title: 'Contribution Table Letter',
      dataIndex: 'categories',
      key: 'categories',
    },
    {
      title: 'Earnings below LEL',
      dataIndex: 'level0',
      key: 'level0',
    },
    {
      title: 'Earnings at or above LEL, up to and including PT',
      dataIndex: 'level1',
      key: 'level1',
    },
    {
      title: 'Earnings above PT up to and including UEL',
      dataIndex: 'level2',
      key: 'level2',
    },
    {
      title: 'Balabce of earnings above UEL',
      dataIndex: 'level3',
      key: 'level3',
    },
  ];

  const columnEmployer = {
    other: [
      {
        title: 'Contribution Table Letter',
        dataIndex: 'categories',
        key: 'categories',
      },
      {
        title: 'Earnings below LEL',
        dataIndex: 'level0',
        key: 'level0',
      },
      {
        title: 'Earnings at or above LEL, up to and including ST',
        dataIndex: 'level1',
        key: 'level1',
      },
      {
        title: 'Earnings above PT up to and including UST/AUST',
        dataIndex: 'level2',
        key: 'level2',
      },
      {
        title: 'Balabce of earnings above UST/AUST',
        dataIndex: 'level3',
        key: 'level3',
      },
    ],
    '2023-2024': [
      {
        title: 'Contribution Table Letter',
        dataIndex: 'categories',
        key: 'categories',
      },
      {
        title: 'Earnings below LEL',
        dataIndex: 'level0',
        key: 'level0',
      },
      {
        title: 'Earnings at or above LEL, up to and including ST',
        dataIndex: 'level1',
        key: 'level1',
      },
      {
        title: 'Earnings above ST up to and including FUST',
        dataIndex: 'level2',
        key: 'level2',
      },
      {
        title: 'Earnings above FUST up to and including UST/AUST/ VUST',
        dataIndex: 'level3',
        key: 'level3',
      },
      {
        title: 'Balabce of earnings above UST/AUST/ VUST',
        dataIndex: 'level4',
        key: 'level4',
      },
    ],
    '2024-2025': [
      {
        title: 'Contribution Table Letter',
        dataIndex: 'categories',
        key: 'categories',
      },
      {
        title: 'Earnings below LEL',
        dataIndex: 'level0',
        key: 'level0',
      },
      {
        title: 'Earnings at or above LEL, up to and including ST',
        dataIndex: 'level1',
        key: 'level1',
      },
      {
        title: 'Earnings above ST up to and including FUST',
        dataIndex: 'level2',
        key: 'level2',
      },
      {
        title: 'Earnings above FUST up to and including UST/AUST/ VUST',
        dataIndex: 'level3',
        key: 'level3',
      },
      {
        title: 'Balabce of earnings above UST/AUST/ VUST',
        dataIndex: 'level4',
        key: 'level4',
      },
    ],
    '2025-2026': [
      {
        title: 'Contribution Table Letter',
        dataIndex: 'categories',
        key: 'categories',
      },
      {
        title: 'Earnings below LEL',
        dataIndex: 'level0',
        key: 'level0',
      },
      {
        title: 'Earnings at or above LEL, up to and including ST',
        dataIndex: 'level1',
        key: 'level1',
      },
      {
        title: 'Earnings above ST up to and including FUST',
        dataIndex: 'level2',
        key: 'level2',
      },
      {
        title: 'Earnings above FUST up to and including UST/AUST/ VUST',
        dataIndex: 'level3',
        key: 'level3',
      },
      {
        title: 'Balabce of earnings above UST/AUST/ VUST',
        dataIndex: 'level4',
        key: 'level4',
      },
    ],
  };

  const data = useMemo(() => {
    if (activeTab === 'CATEGORIES') {
      return (
        dataCategories[selectedTaxYear?.value as keyof typeof dataCategories] ??
        dataCategories['2025-2026'] ??
        dataCategories.other
      );
    } else if (activeTab === 'BAND_WIDTH') {
      return dataBandWidth;
    } else if (activeTab === 'RATES') {
      return (
        dataEmployee[selectedTaxYear?.value as keyof typeof dataEmployee] ??
        dataEmployee['2025-2026'] ??
        []
      );
    }
  }, [activeTab, selectedTaxYear, dataBandWidth]);

  const dataEmployeeRate = useMemo(() => {
    return (
      dataEmployee[selectedTaxYear?.value as keyof typeof dataEmployee] ??
      dataEmployee['2025-2026'] ??
      []
    );
  }, [activeTab, selectedTaxYear]);

  const dataEmployerRate = useMemo(() => {
    return (
      dataEmployer[selectedTaxYear?.value as keyof typeof dataEmployer] ??
      dataEmployer['2025-2026'] ??
      []
    );
  }, [activeTab, selectedTaxYear]);

  const columns = useMemo(() => {
    if (activeTab === 'CATEGORIES') {
      return columnCategories;
    } else if (activeTab === 'BAND_WIDTH') {
      return columnBandWidth;
    } else if (activeTab === 'RATES') {
      return columnEmployee;
    }
  }, [activeTab]);

  const columnEmployerByTaxYear = useMemo(() => {
    return (
      columnEmployer[selectedTaxYear?.value as keyof typeof columnEmployer] ??
      columnEmployer['2025-2026'] ??
      columnEmployer.other
    );
  }, [selectedTaxYear]);

  const items = [
    {
      key: 'employeeTable',
      label: (
        <div>
          <div className="text-[14px]">Employer National Insurance rates</div>
          <div className="text-[12px]">
            {`This table shows how much employers deduct from employee's pay for
            the ${selectedTaxYear.value.replace('-', ' to ')} tax year.`}
          </div>
        </div>
      ),
      children: (
        <AppTableContainer
          rowKey="id"
          columns={columnEmployerByTaxYear}
          data={dataEmployerRate}
          loading={false}
          pagination={false}
          page={1}
          total={0}
          pageSize={0}
          scroll={{ x: 2700 }}
        />
      ),
    },
    {
      key: 'employerTable',
      label: (
        <div>
          <div className="text-[14px]">Employer National Insurance rates</div>
          <div className="text-[12px]">
            {`This table shows how much employers pay towards employee's National Insurance for the ${selectedTaxYear.value.replace('-', ' to ')} tax year.`}
          </div>
        </div>
      ),
      children: (
        <AppTableContainer
          rowKey="id"
          columns={columnEmployee}
          data={dataEmployeeRate}
          loading={false}
          pagination={false}
          page={1}
          total={0}
          pageSize={0}
        />
      ),
    },
  ];

  return (
    <AppCard
      title={
        <div className="flex items-center justify-between">
          <div>NI Bands & Rates</div>
          <div>
            <AppSelect
              options={taxYearOptions}
              onChange={(value: Option<string>) => setSelectedTaxYear(value)}
              defaultValue={taxYearOptions[0]}
            />
          </div>
        </div>
      }
      lineTitleColor="#FFE785"
    >
      <AppCardTabListContainer
        tabList={tabs}
        activeTab={activeTab}
        handleTabChange={handleTabChange}
        className="!border-none"
      >
        <div className="p-[16px]">
          <Spin spinning={activeTab === 'BAND_WIDTH' && loading}>
            {activeTab === 'RATES' ? (
              <AppCollapse
                items={items}
                defaultActiveKey={items.map((item) => item.key)}
                expandIcon={() => (
                  <div className="bg-primary-75 p-[8px] rounded-[8px] w-[32px] h-[32px] flex items-center justify-center">
                    <DownOutlined />
                  </div>
                )}
                style={{ backgroundColor: 'white' }}
              />
            ) : (
              <AppTableContainer
                rowKey="id"
                columns={columns as any}
                data={data as any}
                loading={false}
                pagination={false}
                page={1}
                total={0}
                pageSize={0}
                scroll={activeTab === 'CATEGORIES' ? { x: 400 } : undefined}
              />
            )}
          </Spin>
        </div>
      </AppCardTabListContainer>
    </AppCard>
  );
};

const SSP_SMP_SPP_Other_Rate = () => {
  const taxYearOptions = getTaxYearRateThresholdOptions();
  const [selectedTaxYear, setSelectedTaxYear] = useState<Option<string>>(
    taxYearOptions[0],
  );
  const { value: rateConfigs, loading, run: fetchRates } = useGetRateConfigs();

  useEffect(() => {
    if (selectedTaxYear?.value) {
      fetchRates(parseTaxYearNumber(selectedTaxYear.value));
    }
  }, [selectedTaxYear]);

  const tabs = useMemo(
    () => [
      {
        tab: 'SSP',
        key: 'SSP',
      },
      {
        tab: 'Maternity Pay (SMP)',
        key: 'SMP',
      },
      {
        tab: 'Paternity Pay (SPP)',
        key: 'SPP',
      },
      {
        tab: 'Adoption Pay (SAP)',
        key: 'SAP',
      },
      {
        tab: 'Other',
        key: 'OTHER',
      },
    ],
    [],
  );

  const [activeTab, setActiveTab] = useState('SSP');

  const handleTabChange = (key: string) => {
    setActiveTab(key);
  };

  const sspDataSource = useMemo(() => {
    if (!rateConfigs?.statutoryRates?.ssp || !selectedTaxYear?.value) return [];
    const { weeklyRate, lowerEarningsLimit } = rateConfigs.statutoryRates.ssp;
    const year = parseTaxYearNumber(selectedTaxYear.value);
    return [
      {
        key: '1',
        taxYear: selectedTaxYear.value,
        dateEffective: `06/04/${year}`,
        threshold: String(lowerEarningsLimit),
        from: String(lowerEarningsLimit),
        to: '0.00',
        sspRate: String(weeklyRate),
      },
    ];
  }, [rateConfigs, selectedTaxYear]);

  const columns = [
    {
      title: '',
      dataIndex: 'key',
      width: 60,
    },
    {
      title: 'Tax Year',
      dataIndex: 'taxYear',
      key: 'taxYear',
    },
    {
      title: 'Date Effective',
      dataIndex: 'dateEffective',
      key: 'dateEffective',
    },
    {
      title: 'Threshold',
      dataIndex: 'threshold',
      key: 'threshold',
    },
    {
      title: 'From',
      dataIndex: 'from',
      key: 'from',
    },
    {
      title: 'To',
      dataIndex: 'to',
      key: 'to',
    },
    {
      title: 'SSP Rate',
      dataIndex: 'sspRate',
      key: 'sspRate',
    },
  ];

  const renderTabContent = () => {
    switch (activeTab) {
      case 'SSP':
        return (
          <div className="SMP">
            <AppTableContainer
              rowKey="id"
              columns={columns}
              data={sspDataSource}
              loading={false}
              pagination={false}
              page={1}
              total={0}
              pageSize={0}
            />
            <div className="py-[12px] px-[16px] bg-neutral-50 rounded-[8px] mt-[16px]">
              <div className="text-[14px] mb-[16px]">SSP Days & Limits</div>
              <AppDescription
                column={2}
                labelStyle={{ width: 216 }}
                bordered={false}
                colon={false}
                size="middle"
              >
                <Descriptions.Item label="No. of Waiting Days">
                  {rateConfigs?.statutoryRates?.ssp?.waitingDays ?? 3}
                </Descriptions.Item>
                <Descriptions.Item label="Week of Transfer Form">
                  23
                </Descriptions.Item>
                <Descriptions.Item label="Linkage Period (days)">
                  56
                </Descriptions.Item>
                <Descriptions.Item label="Max. Age for SSP">
                  65
                </Descriptions.Item>
                <Descriptions.Item label="Max. Weeks SSP Payable">
                  28
                </Descriptions.Item>
              </AppDescription>
            </div>
          </div>
        );
      case 'SMP':
        return (
          <div className="SMP">
            <Row gutter={[16, 32]}>
              <Col sm={24} md={12}>
                <div className="text-[14px] mb-[16px]">Standard Rate</div>
                <AppDescription
                  column={1}
                  labelStyle={{ width: 216 }}
                  bordered={false}
                  colon={false}
                  size="middle"
                >
                  <Descriptions.Item label="Rate Payable (Pounds)">
                    {rateConfigs?.statutoryRates?.smp?.standardRate ?? 151.97}
                  </Descriptions.Item>
                  <Descriptions.Item label="Max. Weeks Payable">
                    33
                  </Descriptions.Item>
                </AppDescription>
              </Col>
              <Col sm={24} md={12}>
                <div className="text-[14px] mb-[16px]">
                  Earnings Related Rate
                </div>
                <AppDescription
                  column={1}
                  labelStyle={{ width: 216 }}
                  bordered={false}
                  colon={false}
                  size="middle"
                >
                  <Descriptions.Item label="Rate Payable (%)">
                    90.00
                  </Descriptions.Item>
                  <Descriptions.Item label="Max. Weeks Payable">
                    6
                  </Descriptions.Item>
                </AppDescription>
              </Col>
              <Col sm={24} md={12}>
                <div className="text-[14px] mb-[16px]">Others</div>
                <AppDescription
                  column={1}
                  labelStyle={{ width: 216 }}
                  bordered={false}
                  colon={false}
                  size="middle"
                >
                  <Descriptions.Item label="Amount Recoverable by the Non-SER Employer %">
                    92.00
                  </Descriptions.Item>
                  <Descriptions.Item label="Amount Recoverable by SER Employer %">
                    103.00
                  </Descriptions.Item>
                  <Descriptions.Item label="NIC Compensation Rate For SER Employer %">
                    3.00
                  </Descriptions.Item>
                  <Descriptions.Item label="Maximum KIT Days Allowed">
                    10
                  </Descriptions.Item>
                </AppDescription>
              </Col>
            </Row>
          </div>
        );
      case 'SPP':
        return (
          <div className="SPP">
            <Row gutter={[16, 32]}>
              <Col sm={24} md={12}>
                <div className="text-[14px] mb-[16px]">Standard Rate</div>
                <AppDescription
                  column={1}
                  labelStyle={{ width: 216 }}
                  bordered={false}
                  colon={false}
                  size="middle"
                >
                  <Descriptions.Item label="Rate Payable (Pounds)">
                    {rateConfigs?.statutoryRates?.spp?.standardRate ?? 151.97}
                  </Descriptions.Item>
                  <Descriptions.Item label="Max. Weeks Payable">
                    2
                  </Descriptions.Item>
                </AppDescription>
              </Col>
              <Col sm={24} md={12}>
                <div className="text-[14px] mb-[16px]">
                  Earnings Related Rate
                </div>
                <AppDescription
                  column={1}
                  labelStyle={{ width: 216 }}
                  bordered={false}
                  colon={false}
                  size="middle"
                >
                  <Descriptions.Item label="Rate Payable (%)">
                    90.00
                  </Descriptions.Item>
                </AppDescription>
              </Col>
              <Col sm={24} md={12}>
                <div className="text-[14px] mb-[16px]">Others</div>
                <AppDescription
                  column={1}
                  labelStyle={{ width: 216 }}
                  bordered={false}
                  colon={false}
                  size="middle"
                >
                  <Descriptions.Item label="Amount Recoverable by the Non-SER Employer %">
                    92.00
                  </Descriptions.Item>
                  <Descriptions.Item label="Amount Recoverable by SER Employer %">
                    103.00
                  </Descriptions.Item>
                  <Descriptions.Item label="NIC Compensation Rate For SER Employer %">
                    3.00
                  </Descriptions.Item>
                  <Descriptions.Item label="Maximum KIT Days Allowed">
                    10
                  </Descriptions.Item>
                </AppDescription>
              </Col>
            </Row>
          </div>
        );
      case 'SAP':
        return (
          <div className="SAP">
            <Row gutter={[16, 32]}>
              <Col sm={24} md={12}>
                <div className="text-[14px] mb-[16px]">Standard Rate</div>
                <AppDescription
                  column={1}
                  labelStyle={{ width: 216 }}
                  bordered={false}
                  colon={false}
                  size="middle"
                >
                  <Descriptions.Item label="Rate Payable (Pounds)">
                    {rateConfigs?.statutoryRates?.sap?.standardRate ?? 151.97}
                  </Descriptions.Item>
                  <Descriptions.Item label="Max. Weeks Payable">
                    39
                  </Descriptions.Item>
                </AppDescription>
              </Col>
              <Col sm={24} md={12}>
                <div className="text-[14px] mb-[16px]">
                  Earnings Related Rate
                </div>
                <AppDescription
                  column={1}
                  labelStyle={{ width: 216 }}
                  bordered={false}
                  colon={false}
                  size="middle"
                >
                  <Descriptions.Item label="Rate Payable (%)">
                    90.00
                  </Descriptions.Item>
                </AppDescription>
              </Col>
              <Col sm={24} md={12}>
                <div className="text-[14px] mb-[16px]">Recovery</div>
                <AppDescription
                  column={1}
                  labelStyle={{ width: 216 }}
                  bordered={false}
                  colon={false}
                  size="middle"
                >
                  <Descriptions.Item label="Amount Recoverable by the Non-SER Employer %">
                    92.00
                  </Descriptions.Item>
                  <Descriptions.Item label="Amount Recoverable by SER Employer %">
                    103.00
                  </Descriptions.Item>
                  <Descriptions.Item label="NIC Compensation Rate For SER Employer %">
                    3.00
                  </Descriptions.Item>
                </AppDescription>
              </Col>
            </Row>
          </div>
        );
      case 'OTHER':
        return (
          <div className="OTHER">
            <Row gutter={[16, 32]}>
              <Col sm={24} md={12}>
                <div className="text-[14px] mb-[16px]">Others</div>
                <AppDescription
                  column={1}
                  labelStyle={{ width: 216 }}
                  bordered={false}
                  colon={false}
                  size="middle"
                >
                  <Descriptions.Item label="NI Limits For Small Employer Relief">
                    45000.00
                  </Descriptions.Item>
                </AppDescription>
              </Col>
              <Col span={24}>
                <div className="text-[14px] mb-[16px]">Student Loan</div>
                <div className="w-1/2 max-sm:w-full">
                  <Row className="mb-[16px]">
                    <Col span={6}></Col>
                    <Col span={6}>Type 1</Col>
                    <Col span={6}>Type 2</Col>
                    <Col span={6}>Type 4</Col>
                  </Row>
                  <Row className="mb-[16px]">
                    <Col span={6}>Student Loan Threshold</Col>
                    <Col span={6}>
                      {rateConfigs?.studentLoans?.type1 ?? '19895.00'}
                    </Col>
                    <Col span={6}>
                      {rateConfigs?.studentLoans?.type2 ?? '27295.00'}
                    </Col>
                    <Col span={6}>
                      {rateConfigs?.studentLoans?.type4 ?? '25000.00'}
                    </Col>
                  </Row>
                  <Row>
                    <Col span={6}>Student Loan Rate %</Col>
                    <Col span={6}>9.00</Col>
                    <Col span={6}>9.00</Col>
                    <Col span={6}>9.00</Col>
                  </Row>
                </div>
              </Col>
              <Col sm={24} md={12}>
                <div className="text-[14px] mb-[16px]">Post Graduate Loan</div>
                <AppDescription
                  column={1}
                  labelStyle={{ width: 216 }}
                  bordered={false}
                  colon={false}
                  size="middle"
                >
                  <Descriptions.Item label="Postgraduate Loan Threshold">
                    {rateConfigs?.studentLoans?.psLoan ?? '21000'}
                  </Descriptions.Item>
                  <Descriptions.Item label="Postgraduate Loan Rate %">
                    6
                  </Descriptions.Item>
                  <Descriptions.Item label="NIC Compensation Rate For SER Employer %">
                    3.00
                  </Descriptions.Item>
                </AppDescription>
              </Col>
              <Col sm={24} md={12}>
                <div className="text-[14px] mb-[16px]">Retirement Age</div>
                <AppDescription
                  column={1}
                  labelStyle={{ width: 216 }}
                  bordered={false}
                  colon={false}
                  size="middle"
                >
                  <Descriptions.Item label="Male Retirement Age">
                    65
                  </Descriptions.Item>
                  <Descriptions.Item label="Female Retirement Age">
                    60
                  </Descriptions.Item>
                </AppDescription>
              </Col>
              <Col sm={24} md={12}>
                <div className="text-[14px] mb-[16px]">
                  Employment Allowance
                </div>
                <AppDescription
                  column={1}
                  labelStyle={{ width: 216 }}
                  bordered={false}
                  colon={false}
                  size="middle"
                >
                  <Descriptions.Item label="Employment Allowance limit">
                    4000.00
                  </Descriptions.Item>
                </AppDescription>
              </Col>
              <Col sm={24} md={12}>
                <div className="text-[14px] mb-[16px]">
                  Apprentice Levy Allowance
                </div>
                <AppDescription
                  column={1}
                  labelStyle={{ width: 216 }}
                  bordered={false}
                  colon={false}
                  size="middle"
                >
                  <Descriptions.Item label="Allowance Limit">
                    15000
                  </Descriptions.Item>
                  <Descriptions.Item label="Charge Rate">
                    0.5%
                  </Descriptions.Item>
                </AppDescription>
              </Col>
            </Row>
          </div>
        );
    }
  };

  return (
    <AppCard
      title={
        <div className="flex items-center justify-between">
          <div>SSP SMP SPP & Others</div>
          <div>
            <AppSelect
              options={taxYearOptions}
              onChange={(value: Option<string>) => setSelectedTaxYear(value)}
              defaultValue={taxYearOptions[0]}
            />
          </div>
        </div>
      }
      lineTitleColor="#FFE785"
    >
      <AppCardTabListContainer
        tabList={tabs}
        activeTab={activeTab}
        handleTabChange={handleTabChange}
        className="!border-none"
      >
        <div className="p-[16px]">
          <Spin spinning={loading}>{renderTabContent()}</Spin>
        </div>
      </AppCardTabListContainer>
    </AppCard>
  );
};

const MileageRates = () => {
  const data = [
    {
      key: '1',
      des: 'Petrol up to 1400cc',
      vehicleType: `Petrol up to 1400cc - 101`,
      fuelRate: '0.14',
      mileageRate: '0.45',
      mileageRateAbove10K: '0.25',
    },
    {
      key: '2',
      des: 'Petrol 1401cc to 2000cc',
      vehicleType: `Petrol 1401cc to 2000cc - 102`,
      fuelRate: '0.16',
      mileageRate: '0.45',
      mileageRateAbove10K: '0.25',
    },
    {
      key: '3',
      des: 'Petrol 2001cc and above',
      vehicleType: `Petrol 2001cc and above - 103`,
      fuelRate: '0.24',
      mileageRate: '0.45',
      mileageRateAbove10K: '0.25',
    },
    {
      key: '4',
      des: 'Diesel up to 1400cc',
      vehicleType: `Diesel up to 1400cc - 104`,
      fuelRate: '0.12',
      mileageRate: '0.45',
      mileageRateAbove10K: '0.25',
    },

    {
      key: '5',
      des: 'Diesel 1401cc to 2000cc',
      vehicleType: `Diesel 1401cc to 2000cc - 105`,
      fuelRate: '0.14',
      mileageRate: '0.45',
      mileageRateAbove10K: '0.25',
    },
    {
      key: '6',
      des: 'Diesel 2001cc and above',
      vehicleType: `Diesel 2001cc and above - 106`,
      fuelRate: '0.17',
      mileageRate: '0.45',
      mileageRateAbove10K: '0.25',
    },
    {
      key: '7',
      des: 'LPG up to 1400cc',
      vehicleType: `LPG up to 1400cc - 107`,
      fuelRate: '0.09',
      mileageRate: '0.45',
      mileageRateAbove10K: '0.25',
    },

    {
      key: '8',
      des: 'LPG 1401cc to 2000cc',
      vehicleType: `LPG 1401cc to 2000cc - 108`,
      fuelRate: '0.11',
      mileageRate: '0.45',
      mileageRateAbove10K: '0.25',
    },
    {
      key: '9',
      des: 'LPG 2001cc and above',
      vehicleType: `LPG 2001cc and above - 106`,
      fuelRate: '0.16',
      mileageRate: '0.45',
      mileageRateAbove10K: '0.25',
    },
  ];

  const columns = [
    {
      title: 'ID',
      dataIndex: 'key',
      key: 'id',
      width: 60,
    },
    {
      title: 'Description',
      dataIndex: 'des',
      key: 'description',
    },
    {
      title: 'Vehicle Type',
      dataIndex: 'vehicleType',
      key: 'vehicleType',
    },
    {
      title: 'Fuel Rate',
      dataIndex: 'fuelRate',
      key: 'fuelRate',
    },
    {
      title: 'Mileage Rate',
      dataIndex: 'mileageRate',
      key: 'mileageRate',
    },
    {
      title: 'Mileage Rate Above 10K',
      dataIndex: 'mileageRateAbove10K',
      key: 'mileageRateAbove10K',
    },
  ];

  return (
    <AppCard title="Mileage Rates" lineTitleColor="#FFE785">
      <div>
        <AppTableContainer
          rowKey="id"
          columns={columns}
          data={data}
          loading={false}
          pagination={false}
          page={1}
          total={data.length}
          pageSize={data.length}
        />
      </div>
    </AppCard>
  );
};

const AttachmentOrderList = () => {
  const [selectedRowKey, setSelectedRowKey] = useState<React.Key | null>(null);

  const data = [
    {
      key: '1',
      code: 'A005',
      des: 'Attachment of Earnings Order (Northern Ireland)',
      valueDes: 'ATTACHMENT_OF_EARNINGS_AEO',
      authority: `Magistrates' Courts in E&W`,
      type: 'Fixed Amount',
      disabled: true,
    },
    {
      key: '2',
      code: 'A006',
      des: 'CCAEO (Community Charge) Before 1 Apr 1992 [Table]',
      valueDes: 'CCAEO_COMMUNITY_CHARGE_BEFORE_1_APR_1992_TABLE',
      authority: `Local Authorities in E&W`,
      type: 'Table',
      disabled: true,
    },
    {
      key: '3',
      code: 'A007',
      des: 'CCAEO (Community Charge) From 1 Apr 1992 [Table]',
      valueDes: 'CCAEO_COMMUNITY_CHARGE_FROM_1_APR_1992_TABLE',
      authority: `Local Authorities in E&W`,
      type: 'Table',
      disabled: true,
    },
    {
      key: '4',
      code: 'A008',
      des: 'CTAEO (Council Tax) Before 1 Oct 1998 [Table]',
      valueDes: 'CTAEO_COUNCIL_TAX_BEFORE_1_OCT_1998_TABLE',
      authority: `Local Authorities in E&W`,
      type: 'Table',
      disabled: true,
    },

    {
      key: '5',
      code: 'A009',
      des: 'Attachment of Earnings Order - Fines',
      valueDes: 'CTAEO_COUNCIL_TAX_FROM_1_OCT_98_TO_MAR_07_TABLE',
      authority: `Local Authorities in E&W`,
      type: 'Table',
      disabled: true,
    },
    {
      key: '6',
      code: 'A011',
      des: 'Council Tax Attachment of Earnings Order (CTAEO)',
      valueDes: 'CTAEO_COUNCIL_TAX_FROM_1_APR_2007_TABLE',
      authority: `Local Authorities in E&W`,
      type: 'Table',
      disabled: true,
    },
    {
      key: '7',
      code: 'A013',
      des: 'Earnings Arrestment [Tables] - Refer A027 for 2010',
      valueDes: 'EARNINGS ARRESTMENT_TABLES_REFER_A027_FOR_2010',
      authority: `Sheriff Court in Scotland`,
      type: 'Table',
      disabled: true,
    },
    {
      key: '8',
      code: 'A016',
      des: 'Conjoined Arrestment Order (CAO) [Table]',
      valueDes: 'CONJOINED_ARRESTMENT ORDER_CAO_TABLE',
      authority: `Sheriff Court in Scotland`,
      type: 'Table',
      disabled: true,
    },
    {
      key: '9',
      code: 'A028',
      des: 'Direct Earnings Attachment (Standard Rate)',
      valueDes: 'DIRECT_EARNINGS_ATTACHMENT_STANDARD_DATE',
      authority: `Department for Work & Pensions`,
      type: 'Table',
      disabled: false,
    },
    {
      key: '10',
      code: 'A030',
      des: 'Earnings Arrestment (Scotland)',
      valueDes: 'EARNINGS_ARRESTMENT_TABLE_2014',
      authority: `Sheriff Court in Scotland`,
      type: 'Table',
      disabled: true,
    },
    {
      key: '11',
      code: 'A031',
      des: 'Direct Earnings Attachment (Higher Rate)',
      valueDes: 'DIRECT_EARNINGS_ATTACHMENT_HIGHER_RATE',
      authority: 'Department for Work & Pensions',
      type: 'Table',
      disabled: false,
    },
    {
      key: '12',
      code: 'DEO',
      des: 'Child Support Deduction from Earnings (DEO)',
      valueDes: 'CHILD_SUPPORT_DEDUCTION_FROM_EARNINGS',
      authority: 'Department for Work & Pensions',
      type: 'Fixed Amount',
      disabled: false,
    },
  ];

  const columns = [
    {
      title: 'Description',
      dataIndex: 'des',
      key: 'description',
    },
    {
      title: 'Authority',
      dataIndex: 'authority',
      key: 'authority',
    },
    {
      title: 'Type',
      dataIndex: 'type',
      key: 'type',
    },
  ];

  const columnCode = [
    {
      title: 'Code',
      dataIndex: 'code',
      key: 'code',
    },
    {
      title: 'Frequency',
      dataIndex: 'frequency',
      key: 'frequency',
    },
    {
      title: 'Type',
      dataIndex: 'type',
      key: 'type',
    },
    {
      title: 'From',
      dataIndex: 'from',
      key: 'from',
    },
    {
      title: 'To',
      dataIndex: 'to',
      key: 'to',
    },
    {
      title: 'Deduction',
      dataIndex: 'deduction',
      key: 'deduction',
    },
  ];

  return (
    <AppCard title="Attachment Order List" lineTitleColor="#FFE785">
      <div>
        <div>
          <AppTableContainer
            rowKey="key"
            columns={columns}
            data={data}
            loading={false}
            pagination={false}
            page={1}
            total={data.length}
            pageSize={data.length}
            scroll={{ x: 400 }}
            onRow={(record: any) => ({
              onClick: () => {
                setSelectedRowKey(record.code);
              },
              style: { cursor: 'pointer' },
            })}
            rowClassName={(record: any) =>
              selectedRowKey === record.code
                ? 'ant-table-row-selected-custom'
                : ''
            }
          />
          {!!dataSubTable[selectedRowKey as keyof typeof dataSubTable]
            ?.length && (
            <AppTableContainer
              className="mt-[24px]"
              rowKey="key"
              columns={columnCode}
              data={dataSubTable[selectedRowKey as keyof typeof dataSubTable]}
              loading={false}
              pagination={false}
              page={1}
              total={1}
              pageSize={1}
              scroll={{ x: 400 }}
            />
          )}
        </div>
      </div>
    </AppCard>
  );
};

export default RateBands;
